import React, { useState, useEffect, useCallback } from 'react';
import { createPropertyInquiry } from '../../../services/propertyInquiryService';
import { getAllNotaries, getNotariesBySpecialization } from '../../../services/notaryService';
import { getAppointmentsByDateRange } from '../../../services/appointmentService';

// Abidjan communes for location matching
const ABIDJAN_COMMUNES = [
  'Cocody', 'Plateau', 'Marcory', 'Treichville', 'Adjamé',
  'Abobo', 'Yopougon', 'Koumassi', 'Port-Bouët', 'Attécoubé',
  'Bingerville', 'Anyama', 'Songon', 'Bassam',
];

const CONSULTATION_TYPES = [
  { key: 'achat', label: 'Achat immobilier', specialization: 'immobilier' },
  { key: 'vente', label: 'Vente immobilier', specialization: 'immobilier' },
  { key: 'succession', label: 'Succession / Héritage', specialization: 'succession' },
  { key: 'bail', label: 'Bail / Location', specialization: 'bail' },
  { key: 'hypotheque', label: 'Hypothèque / Crédit', specialization: 'hypotheque' },
  { key: 'general', label: 'Consultation générale', specialization: null },
];

// Generate time slots for a given date (business hours 09:00–17:00, skip 12:00–13:00)
const generateSlots = (dateStr) => {
  const slots = [];
  let cur = new Date(`${dateStr}T09:00:00`);
  const end = new Date(`${dateStr}T17:00:00`);
  while (cur < end) {
    const h = cur?.getHours();
    const m = cur?.getMinutes();
    const timeStr = `${String(h)?.padStart(2, '0')}:${String(m)?.padStart(2, '0')}`;
    if (!(h === 12 || (h === 12 && m < 60))) {
      if (h !== 12) slots?.push(timeStr);
    }
    cur?.setMinutes(cur?.getMinutes() + 30);
  }
  return slots;
};

// Get next 5 weekdays from today
const getNextWeekdays = () => {
  const days = [];
  const today = new Date();
  let i = 1;
  while (days?.length < 5) {
    const d = new Date(today);
    d?.setDate(today?.getDate() + i);
    if (d?.getDay() !== 0 && d?.getDay() !== 6) {
      days?.push(d?.toISOString()?.split('T')?.[0]);
    }
    i++;
  }
  return days;
};

const inputStyle = {
  width: '100%',
  padding: '12px 14px',
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px',
  color: '#fff',
  fontSize: '13px',
  fontFamily: 'DM Sans, sans-serif',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s, box-shadow 0.2s',
};

const selectStyle = {
  ...inputStyle,
  background: 'rgba(17,17,17,0.98)',
  cursor: 'pointer',
};

const labelStyle = {
  display: 'block',
  color: 'rgba(255,255,255,0.4)',
  fontSize: '10px',
  marginBottom: '6px',
  letterSpacing: '0.1em',
  fontWeight: 'bold',
  textTransform: 'uppercase',
};

const ExitInquiryModal = ({ property, onClose, onContinueTour }) => {
  const [activeTab, setActiveTab] = useState('inquiry'); // 'inquiry' | 'notary'
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '', preferredTime: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Notary suggestion state
  const [userCommune, setUserCommune] = useState('');
  const [consultationType, setConsultationType] = useState('');
  const [suggestedNotaries, setSuggestedNotaries] = useState([]);
  const [loadingNotaries, setLoadingNotaries] = useState(false);
  const [selectedNotary, setSelectedNotary] = useState(null);

  // Availability state
  const [availabilityDate, setAvailabilityDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState('');

  const weekdays = getNextWeekdays();

  // Fetch notaries when commune or consultation type changes
  const fetchSuggestedNotaries = useCallback(async () => {
    if (!userCommune && !consultationType) return;
    setLoadingNotaries(true);
    setSuggestedNotaries([]);
    setSelectedNotary(null);
    setAvailabilityDate('');
    setSelectedSlot('');
    try {
      const consType = CONSULTATION_TYPES?.find((c) => c?.key === consultationType);
      let result;
      if (consType?.specialization) {
        result = await getNotariesBySpecialization(consType?.specialization);
      } else {
        result = await getAllNotaries();
      }
      let notaries = result?.data || [];
      // Filter by commune proximity if commune selected
      if (userCommune) {
        const byCommune = notaries?.filter((n) =>
          n?.office_address?.toLowerCase()?.includes(userCommune?.toLowerCase())
        );
        notaries = byCommune?.length > 0 ? byCommune : notaries;
      }
      setSuggestedNotaries(notaries?.slice(0, 4));
    } catch {
      setSuggestedNotaries([]);
    } finally {
      setLoadingNotaries(false);
    }
  }, [userCommune, consultationType]);

  useEffect(() => {
    if (activeTab === 'notary' && (userCommune || consultationType)) {
      fetchSuggestedNotaries();
    }
  }, [activeTab, userCommune, consultationType, fetchSuggestedNotaries]);

  // Fetch booked slots when notary + date selected
  useEffect(() => {
    if (!selectedNotary || !availabilityDate) {
      setAvailableSlots([]);
      setBookedSlots([]);
      setSelectedSlot('');
      return;
    }
    setLoadingSlots(true);
    const startOfDay = `${availabilityDate}T00:00:00`;
    const endOfDay = `${availabilityDate}T23:59:59`;
    getAppointmentsByDateRange(startOfDay, endOfDay)?.then((res) => {
      const appts = res?.data || [];
      const taken = appts?.filter((a) => a?.notary_id === selectedNotary?.id)?.map((a) => {
          const d = new Date(a?.scheduled_date);
          return `${String(d?.getHours())?.padStart(2, '0')}:${String(d?.getMinutes())?.padStart(2, '0')}`;
        });
      setBookedSlots(taken);
      const allSlots = generateSlots(availabilityDate);
      setAvailableSlots(allSlots);
      setSelectedSlot('');
      setLoadingSlots(false);
    })?.catch(() => {
      setAvailableSlots(generateSlots(availabilityDate));
      setBookedSlots([]);
      setLoadingSlots(false);
    });
  }, [selectedNotary, availabilityDate]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e?.target?.name]: e?.target?.value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.name || !form?.phone) return;
    setSubmitting(true);
    setError(null);
    try {
      const inquiryType = activeTab === 'notary' ? 'consultation notariale' : 'renseignements';
      const notaryInfo = selectedNotary
        ? ` Notaire sélectionné: ${selectedNotary?.notary_name || selectedNotary?.user_profile?.full_name}.`
        : '';
      const slotInfo = selectedSlot && availabilityDate
        ? ` Créneau souhaité: ${new Date(availabilityDate)?.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} à ${selectedSlot}.`
        : '';
      const consTypeInfo = consultationType
        ? ` Type: ${CONSULTATION_TYPES?.find((c) => c?.key === consultationType)?.label || consultationType}.`
        : '';
      let result = await createPropertyInquiry({
        message: form?.message ||
          (activeTab === 'notary'
            ? `Demande de consultation notariale suite à la visite 3D de: ${property?.title || 'la propriété'}. Contact: ${form?.name}, ${form?.phone}${form?.email ? ', ' + form?.email : ''}.${consTypeInfo}${notaryInfo}${slotInfo}${form?.preferredTime ? ' Disponibilité: ' + form?.preferredTime : ''}`
            : `Demande de renseignements suite à la visite 3D de: ${property?.title || 'la propriété'}. Contact: ${form?.name}, ${form?.phone}${form?.email ? ', ' + form?.email : ''}${form?.preferredTime ? '. Disponibilité: ' + form?.preferredTime : ''}`),
        propertyId: property?.id || null,
        phone: form?.phone,
        preferred_contact_time: selectedSlot
          ? `${availabilityDate} ${selectedSlot}`
          : form?.preferredTime || null,
        type: inquiryType,
      });
      if (result?.error) {
        setError('Une erreur est survenue. Veuillez réessayer.');
      } else {
        setSubmitted(true);
      }
    } catch {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  const freeSlots = availableSlots?.filter((s) => !bookedSlots?.includes(s));

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        background: 'rgba(5,5,5,0.85)',
        backdropFilter: 'blur(20px)',
        fontFamily: 'DM Sans, sans-serif',
        overflowY: 'auto',
      }}
    >
      {/* Animated background grid */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none',
        backgroundImage: 'linear-gradient(rgba(212,175,55,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.05) 1px, transparent 1px)',
        backgroundSize: '48px 48px',
      }} />
      <div style={{
        position: 'relative',
        width: '100%',
        maxWidth: activeTab === 'notary' ? '560px' : '480px',
        background: '#0A0A0A',
        border: '1px solid rgba(212,175,55,0.2)',
        borderRadius: '24px',
        overflow: 'hidden',
        boxShadow: '0 0 50px rgba(212,175,55,0.1), 0 20px 40px rgba(0,0,0,0.8)',
        margin: 'auto',
      }}>
        {/* Gold top accent */}
        <div style={{ height: '3px', background: 'linear-gradient(90deg, transparent, #D4AF37, transparent)' }} />

        {/* Header */}
        <div style={{ padding: '24px 28px 0' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div style={{
                width: '44px', height: '44px', borderRadius: '12px',
                background: 'rgba(212,175,55,0.1)',
                border: '1px solid rgba(212,175,55,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '20px',
                boxShadow: 'inset 0 0 10px rgba(212,175,55,0.1)'
              }}>
                🏛️
              </div>
              <div>
                <h2 style={{ color: '#D4AF37', fontSize: '18px', fontWeight: 'bold', margin: 0, letterSpacing: '0.02em' }}>
                  Intéressé par cette propriété ?
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', margin: '4px 0 0', lineHeight: 1.4 }}>
                  {property?.title || 'Propriété'} · Kapital States
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '10px',
                width: '36px', height: '36px',
                color: 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                fontSize: '16px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '10px', padding: '20px 28px 0' }}>
          {[
            { key: 'inquiry', label: '💬 Demande d\'info' },
            { key: 'notary', label: '⚖️ Consultation notaire' },
          ]?.map((tab) => (
            <button
              key={tab?.key}
              onClick={() => setActiveTab(tab?.key)}
              style={{
                flex: 1,
                padding: '12px 10px',
                borderRadius: '12px',
                border: activeTab === tab?.key
                  ? '1px solid rgba(212,175,55,0.5)'
                  : '1px solid rgba(255,255,255,0.08)',
                background: activeTab === tab?.key
                  ? 'rgba(212,175,55,0.1)'
                  : 'rgba(255,255,255,0.02)',
                color: activeTab === tab?.key ? '#D4AF37' : 'rgba(255,255,255,0.4)',
                fontSize: '13px',
                fontWeight: activeTab === tab?.key ? 'bold' : 'normal',
                cursor: 'pointer',
                transition: 'all 0.2s',
                letterSpacing: '0.02em',
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              {tab?.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ padding: '24px 28px 32px' }}>
          {submitted ? (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                background: 'rgba(212,175,55,0.1)',
                border: '2px solid rgba(212,175,55,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '32px', margin: '0 auto 20px',
                boxShadow: '0 0 20px rgba(212,175,55,0.2)'
              }}>
                ✓
              </div>
              <h3 style={{ color: '#D4AF37', fontSize: '20px', fontWeight: 'bold', margin: '0 0 12px' }}>
                Demande envoyée avec succès !
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: 1.6, margin: '0 auto 28px', maxWidth: '380px' }}>
                {activeTab === 'notary'
                  ? selectedNotary
                    ? `${selectedNotary?.notary_name || 'Le notaire'} vous contactera pour confirmer votre rendez-vous${selectedSlot ? ` du ${new Date(availabilityDate)?.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} à ${selectedSlot}` : ''}.`
                    : 'Un notaire partenaire vous contactera dans les 24h pour organiser votre consultation.' :'Notre équipe vous contactera très prochainement avec toutes les informations.'}
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={onContinueTour}
                  style={{
                    flex: 1, padding: '14px', borderRadius: '12px',
                    border: '1px solid rgba(212,175,55,0.4)', background: 'rgba(212,175,55,0.1)',
                    color: '#D4AF37', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer',
                    fontFamily: 'DM Sans, sans-serif', transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(212,175,55,0.2)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(212,175,55,0.1)'}
                >
                  ▶ Continuer la visite
                </button>
                <button
                  onClick={onClose}
                  style={{
                    flex: 1, padding: '14px', borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)',
                    color: 'rgba(255,255,255,0.6)', fontSize: '14px', cursor: 'pointer',
                    fontFamily: 'DM Sans, sans-serif', transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
                >
                  Fermer
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Context banner */}
              <div style={{
                background: 'rgba(212,175,55,0.05)',
                border: '1px solid rgba(212,175,55,0.2)',
                borderRadius: '12px',
                padding: '12px 16px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}>
                <span style={{ fontSize: '20px' }}>
                  {activeTab === 'notary' ? '⚖️' : '🏠'}
                </span>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', margin: 0, lineHeight: 1.5 }}>
                  {activeTab === 'notary' ?'Trouvez un notaire disponible près de chez vous selon votre type de consultation.' :'Vous venez de visiter cette propriété en 3D. Nos conseillers sont disponibles pour répondre à vos questions.'}
                </p>
              </div>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                {/* ── NOTARY TAB: Location + Consultation Type Filters ── */}
                {activeTab === 'notary' && (
                  <>
                    {/* Location + Consultation type row */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={labelStyle}>VOTRE COMMUNE</label>
                        <select
                          value={userCommune}
                          onChange={(e) => setUserCommune(e?.target?.value)}
                          style={selectStyle}
                        >
                          <option value="">Toutes communes</option>
                          {ABIDJAN_COMMUNES?.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={labelStyle}>TYPE DE CONSULTATION</label>
                        <select
                          value={consultationType}
                          onChange={(e) => setConsultationType(e?.target?.value)}
                          style={selectStyle}
                        >
                          <option value="">Tous types</option>
                          {CONSULTATION_TYPES?.map((c) => (
                            <option key={c?.key} value={c?.key}>{c?.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Notary suggestions */}
                    <div>
                      <label style={{ ...labelStyle, marginBottom: '10px' }}>
                        NOTAIRES DISPONIBLES
                        {(userCommune || consultationType) && !loadingNotaries && (
                          <span style={{ color: '#D4AF37', marginLeft: '8px', fontWeight: 'normal' }}>
                            ({suggestedNotaries?.length} trouvé{suggestedNotaries?.length !== 1 ? 's' : ''})
                          </span>
                        )}
                      </label>

                      {loadingNotaries ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '16px', color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>
                          <div style={{
                            width: '16px', height: '16px', borderRadius: '50%',
                            border: '2px solid rgba(212,175,55,0.2)', borderTopColor: '#D4AF37',
                            animation: 'spin 0.8s linear infinite',
                          }} />
                          Recherche des notaires disponibles…
                        </div>
                      ) : suggestedNotaries?.length === 0 ? (
                        <div style={{
                          padding: '16px', background: 'rgba(255,255,255,0.02)',
                          border: '1px dashed rgba(255,255,255,0.1)', borderRadius: '12px',
                          color: 'rgba(255,255,255,0.4)', fontSize: '13px', textAlign: 'center',
                        }}>
                          {userCommune || consultationType
                            ? 'Aucun notaire trouvé pour ces critères. Modifiez vos filtres.' :'Sélectionnez votre commune ou type de consultation pour voir les notaires.'}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {suggestedNotaries?.map((notary) => {
                            const isSelected = selectedNotary?.id === notary?.id;
                            const name = notary?.notary_name || notary?.user_profile?.full_name || 'Notaire';
                            const address = notary?.office_address || '';
                            const specs = notary?.specializations || [];
                            return (
                              <button
                                key={notary?.id}
                                type="button"
                                onClick={() => {
                                  setSelectedNotary(isSelected ? null : notary);
                                  setAvailabilityDate('');
                                  setSelectedSlot('');
                                }}
                                style={{
                                  display: 'flex', alignItems: 'flex-start', gap: '12px',
                                  padding: '12px 14px', borderRadius: '12px',
                                  border: isSelected ? '1px solid rgba(212,175,55,0.5)' : '1px solid rgba(255,255,255,0.08)',
                                  background: isSelected ? 'rgba(212,175,55,0.08)' : 'rgba(255,255,255,0.02)',
                                  cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s', width: '100%',
                                }}
                                onMouseEnter={(e) => { if(!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                                onMouseLeave={(e) => { if(!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
                              >
                                <div style={{
                                  width: '36px', height: '36px', borderRadius: '10px',
                                  background: isSelected ? 'rgba(212,175,55,0.2)' : 'rgba(255,255,255,0.05)',
                                  border: isSelected ? '1px solid rgba(212,175,55,0.4)' : '1px solid rgba(255,255,255,0.1)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0,
                                }}>
                                  ⚖️
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                    <span style={{ color: isSelected ? '#D4AF37' : '#fff', fontSize: '14px', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {name}
                                    </span>
                                    {isSelected && (
                                      <span style={{ color: '#D4AF37', fontSize: '11px', flexShrink: 0, fontWeight: 'bold' }}>✓ Sélectionné</span>
                                    )}
                                  </div>
                                  {address && (
                                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', marginTop: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      📍 {address}
                                    </div>
                                  )}
                                  {specs?.length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                                      {specs?.slice(0, 3)?.map((s, idx) => (
                                        <span key={idx} style={{
                                          padding: '3px 8px', borderRadius: '6px', background: 'rgba(212,175,55,0.08)',
                                          border: '1px solid rgba(212,175,55,0.15)', color: 'rgba(212,175,55,0.8)', fontSize: '10px', letterSpacing: '0.03em', textTransform: 'capitalize'
                                        }}>
                                          {s}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Real-time availability calendar */}
                    {selectedNotary && (
                      <div style={{
                        background: 'rgba(212,175,55,0.03)', border: '1px solid rgba(212,175,55,0.15)',
                        borderRadius: '16px', padding: '16px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                          <span style={{ fontSize: '16px' }}>📅</span>
                          <span style={{ color: '#D4AF37', fontSize: '12px', fontWeight: 'bold', letterSpacing: '0.08em' }}>
                            DISPONIBILITÉS — {(selectedNotary?.notary_name || selectedNotary?.user_profile?.full_name || 'Notaire')?.toUpperCase()}
                          </span>
                        </div>

                        {/* Date selector */}
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                          {weekdays?.map((d) => {
                            const dateObj = new Date(d + 'T12:00:00');
                            const isSelected = availabilityDate === d;
                            return (
                              <button
                                key={d}
                                type="button"
                                onClick={() => setAvailabilityDate(d)}
                                style={{
                                  padding: '8px 12px', borderRadius: '10px',
                                  border: isSelected ? '1px solid rgba(212,175,55,0.6)' : '1px solid rgba(255,255,255,0.1)',
                                  background: isSelected ? 'rgba(212,175,55,0.15)' : 'rgba(255,255,255,0.02)',
                                  color: isSelected ? '#D4AF37' : 'rgba(255,255,255,0.5)', fontSize: '12px',
                                  fontWeight: isSelected ? 'bold' : 'normal', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                                  transition: 'all 0.2s', textAlign: 'center', minWidth: '60px',
                                }}
                              >
                                <div style={{ textTransform: 'capitalize' }}>{dateObj?.toLocaleDateString('fr-FR', { weekday: 'short' })}</div>
                                <div style={{ fontSize: '14px', fontWeight: 'bold', marginTop: '2px' }}>
                                  {dateObj?.toLocaleDateString('fr-FR', { day: 'numeric' })}
                                </div>
                              </button>
                            );
                          })}
                        </div>

                        {/* Time slots */}
                        {!availabilityDate ? (
                          <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '13px', margin: 0, textAlign: 'center', padding: '12px 0' }}>
                            Sélectionnez une date pour voir les créneaux disponibles
                          </p>
                        ) : loadingSlots ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '12px', color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>
                            <div style={{
                              width: '14px', height: '14px', borderRadius: '50%',
                              border: '2px solid rgba(212,175,55,0.2)', borderTopColor: '#D4AF37', animation: 'spin 0.8s linear infinite',
                            }} />
                            Chargement des créneaux…
                          </div>
                        ) : freeSlots?.length === 0 ? (
                          <p style={{ color: '#ef4444', fontSize: '13px', margin: 0, textAlign: 'center', padding: '12px 0' }}>
                            Aucun créneau disponible ce jour. Choisissez une autre date.
                          </p>
                        ) : (
                          <>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {availableSlots?.map((slot) => {
                                const isBooked = bookedSlots?.includes(slot);
                                const isChosen = selectedSlot === slot;
                                return (
                                  <button
                                    key={slot}
                                    type="button"
                                    disabled={isBooked}
                                    onClick={() => setSelectedSlot(isChosen ? '' : slot)}
                                    style={{
                                      padding: '8px 12px', borderRadius: '8px',
                                      border: isChosen ? '1px solid rgba(212,175,55,0.8)' : isBooked ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(255,255,255,0.15)',
                                      background: isChosen ? 'rgba(212,175,55,0.2)' : isBooked ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.03)',
                                      color: isChosen ? '#D4AF37' : isBooked ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.8)',
                                      fontSize: '13px', fontWeight: isChosen ? 'bold' : 'normal',
                                      cursor: isBooked ? 'not-allowed' : 'pointer', fontFamily: 'DM Sans, sans-serif',
                                      textDecoration: isBooked ? 'line-through' : 'none', transition: 'all 0.2s',
                                    }}
                                  >
                                    {slot}
                                  </button>
                                );
                              })}
                            </div>
                            {selectedSlot && (
                              <div style={{
                                marginTop: '12px', padding: '10px 14px', borderRadius: '10px',
                                background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.3)',
                                color: '#D4AF37', fontSize: '13px', fontWeight: 'bold',
                              }}>
                                ✓ Créneau réservé : {new Date(availabilityDate + 'T12:00:00')?.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} à {selectedSlot}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Name + Phone row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={labelStyle}>NOM & PRÉNOMS *</label>
                    <input
                      name="name"
                      value={form?.name}
                      onChange={handleChange}
                      required
                      placeholder="Ex: Jean Dupont"
                      style={inputStyle}
                      onFocus={(e) => e.target.style.borderColor = 'rgba(212,175,55,0.5)'}
                      onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>TÉLÉPHONE *</label>
                    <input
                      name="phone"
                      value={form?.phone}
                      onChange={handleChange}
                      required
                      placeholder="+225 07..."
                      style={inputStyle}
                      onFocus={(e) => e.target.style.borderColor = 'rgba(212,175,55,0.5)'}
                      onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label style={labelStyle}>ADRESSE EMAIL</label>
                  <input
                    name="email"
                    type="email"
                    value={form?.email}
                    onChange={handleChange}
                    placeholder="votre@email.com"
                    style={inputStyle}
                    onFocus={(e) => e.target.style.borderColor = 'rgba(212,175,55,0.5)'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                  />
                </div>

                {/* Preferred time — only for inquiry tab */}
                {activeTab === 'inquiry' && (
                  <div>
                    <label style={labelStyle}>MOMENT PRÉFÉRÉ POUR ÊTRE CONTACTÉ</label>
                    <select
                      name="preferredTime"
                      value={form?.preferredTime}
                      onChange={handleChange}
                      style={{ ...selectStyle, color: form?.preferredTime ? '#fff' : 'rgba(255,255,255,0.3)' }}
                      onFocus={(e) => e.target.style.borderColor = 'rgba(212,175,55,0.5)'}
                      onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                    >
                      <option value="">Peu importe</option>
                      <option value="matin">Matin (8h–12h)</option>
                      <option value="apres-midi">Après-midi (12h–17h)</option>
                      <option value="soir">Soir (17h–20h)</option>
                      <option value="weekend">Week-end</option>
                    </select>
                  </div>
                )}

                {/* Message */}
                <div>
                  <label style={labelStyle}>VOTRE MESSAGE</label>
                  <textarea
                    name="message"
                    value={form?.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder={activeTab === 'notary' ?'Précisez votre besoin (achat, vente, conseil succession...)' :'Posez vos questions ou laissez un commentaire sur cette propriété...'}
                    style={{
                      ...inputStyle,
                      resize: 'vertical',
                      minHeight: '100px',
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'rgba(212,175,55,0.5)'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                  />
                </div>

                {error && (
                  <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', padding: '10px', borderRadius: '8px', color: '#ef4444', fontSize: '13px', textAlign: 'center' }}>
                    {error}
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                  <button
                    type="submit"
                    disabled={submitting || !form?.name || !form?.phone}
                    style={{
                      flex: 2, padding: '14px', borderRadius: '12px', border: 'none',
                      background: submitting || !form?.name || !form?.phone ? 'rgba(212,175,55,0.2)' : '#D4AF37',
                      color: submitting || !form?.name || !form?.phone ? 'rgba(255,255,255,0.3)' : '#000',
                      fontSize: '14px', fontWeight: 'bold', cursor: submitting || !form?.name || !form?.phone ? 'not-allowed' : 'pointer',
                      letterSpacing: '0.02em', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.3s',
                      boxShadow: submitting || !form?.name || !form?.phone ? 'none' : '0 0 20px rgba(212,175,55,0.3)',
                    }}
                    onMouseEnter={(e) => { if (!submitting && form?.name && form?.phone) { e.currentTarget.style.boxShadow = '0 0 30px rgba(212,175,55,0.5)'; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
                    onMouseLeave={(e) => { if (!submitting && form?.name && form?.phone) { e.currentTarget.style.boxShadow = '0 0 20px rgba(212,175,55,0.3)'; e.currentTarget.style.transform = 'translateY(0)'; } }}
                  >
                    {submitting ? 'Envoi en cours…' : activeTab === 'notary' ? '⚖️ Valider la réservation' : '✉️ Envoyer ma demande'}
                  </button>
                  <button
                    type="button"
                    onClick={onContinueTour}
                    style={{
                      flex: 1, padding: '14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)',
                      background: 'rgba(255,255,255,0.03)', color: 'rgba(255,255,255,0.6)', fontSize: '13px',
                      cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
                  >
                    ▶ Continuer la visite
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
      <style>{`
        input::placeholder, textarea::placeholder { color: rgba(255,255,255,0.2) !important; font-weight: normal; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default ExitInquiryModal;